"use client";

import { createContext, useContext, useCallback, useRef } from "react";

interface SavedState {
  saved: boolean;
}

type SavedChangeListener = (itemId: string, itemType: "post" | "listing", saved: boolean) => void;

interface SavedContextType {
  notifySavedChange: (itemId: string, itemType: "post" | "listing", saved: boolean) => void;
  subscribeSavedChange: (callback: SavedChangeListener) => () => void;
  getSavedState: (itemId: string, itemType: "post" | "listing") => SavedState | null;
  setSavedState: (itemId: string, itemType: "post" | "listing", saved: boolean) => void;
}

const SavedContext = createContext<SavedContextType | undefined>(undefined);

const getStateKey = (itemId: string, itemType: "post" | "listing") => `${itemType}-${itemId}`;

export function SavedProvider({ children }: { children: React.ReactNode }) {
  const statesRef = useRef(new Map<string, SavedState>());
  const listenersRef = useRef(new Set<SavedChangeListener>());

  const getSavedState = useCallback((itemId: string, itemType: "post" | "listing") => {
    return statesRef.current.get(getStateKey(itemId, itemType)) || null;
  }, []);

  const setSavedState = useCallback((itemId: string, itemType: "post" | "listing", saved: boolean) => {
    statesRef.current.set(getStateKey(itemId, itemType), { saved });
  }, []);

  const notifySavedChange = useCallback((itemId: string, itemType: "post" | "listing", saved: boolean) => {
    statesRef.current.set(getStateKey(itemId, itemType), { saved });
    listenersRef.current.forEach(listener => listener(itemId, itemType, saved));
  }, []);

  const subscribeSavedChange = useCallback((callback: SavedChangeListener) => {
    listenersRef.current.add(callback);
    return () => { listenersRef.current.delete(callback); };
  }, []);

  return (
    <SavedContext.Provider value={{ notifySavedChange, subscribeSavedChange, getSavedState, setSavedState }}>
      {children}
    </SavedContext.Provider>
  );
}

export function useSaved() {
  const context = useContext(SavedContext);
  if (!context) {
    throw new Error("useSaved must be used within a SavedProvider");
  }
  return context;
}
